/**
 * Project configuration
 */

export const PROJECT_COMMANDS = {
    EXPORT_CODE: 'export-code',
    BUILD_PROJECT: 'build-project',
    CLEAN_AND_BUILD_PROJECT: 'build-and-clean-project',
    CLEAR_CACHE: 'clear-cache',
    TOOLCHAIN_SETTINGS: 'toolchain-settings'
} as const;

export const PROJECT_OPERATIONS = {
    GENERATING_CODE: 'generating code',
    BUILDING_PROJECT: 'building project',
    CLEANING_PROJECT: 'cleaning project',
    CLEARING_CACHE: 'clearing cache',
    VALIDATING_CONTAINER: 'validating container'
} as const;

export type PROJECT_COMMANDS = (typeof PROJECT_COMMANDS)[keyof typeof PROJECT_COMMANDS];

export type ProjectConfigSettings = {
    resourcesPath?: string;
    buildPath?: string;
    outputPath?: string;
    [key: string]: any;
};

export type ProjectConfig = {
    name: string;
    path: string;
    type: string;
    version: string;
    description?: string;
    author?: string;
    created: Date;
    modified: Date;
    settings: ProjectConfigSettings;
};

/**
 * Project operation result
 */
export type ProjectOperationResult = {
    success: boolean;
    error: string | null;
    data?: any;
};

export type RecentProject = {
    path: string;
    name: string;
    shortenedPath: string;
    lastOpened: Date;
};

export type SelectedFile = {
    filePath: string;
    isSaved: boolean;
    content?: string; // Optional to support both use cases
};

/**
 * XML file types
 */
export type XMLFileType = 'widget' | 'component' | 'globals' | 'project' | 'test' | 'screen' | 'other' | null;

import type { EcosystemId, ProjectLvglVersion, ProjectTheme, UiProjectTemplate } from '../project-creation/ecosystems';
import type { ProjectTemplateId } from '../project-creation/project-templates';

export type ProjectLicenseMetadata = {
    product_id?: string;
    git_url?: string;
};

export type GitWorkspaceInfo = {
    gitInstalled: boolean;
    isRepository: boolean;
    remoteUrls: string[];
};

// Options for creating a project
export type CreateProjectOptions = {
    componentLib?: boolean; // Should be true if creating a component library
    /** Bundled template under resources/templates/; defaults to blank new-project template */
    templateId?: ProjectTemplateId;
    ecosystemId?: EcosystemId;
    uiTemplate?: UiProjectTemplate;
    lvglVersion?: ProjectLvglVersion;
    theme?: ProjectTheme;
    displayWidth?: number;
    displayHeight?: number;
    ecosystemParameters?: Record<string, boolean | number | string>;
    licenseMetadata?: ProjectLicenseMetadata;
};

/** Options for the create-project folder picker (save dialog) */
export type OpenSaveDialogOptions = {
    templateId?: ProjectTemplateId;
    licenseMetadata?: ProjectLicenseMetadata;
};

export type ResponsiveTarget = {
    name: string;
    id: number;
};

export type Display = {
    width: number;
    height: number;
    id?: number;
    name?: string;
    zoom?: number;
    language?: string;
    target: ResponsiveTarget;
    colorFormat?: string;
    style: Record<string, string>;
};

/**
 * Canvas props
 */
export type CanvasProps = {
    width: number;
    height: number;
    zoom?: number;
    language?: string;
    target: ResponsiveTarget;
    style: Record<string, string>;
};

export type Dimensions = {
    width: number;
    height: number;
};

/**
 * LVGL object area coordinates
 */
export type LVGLObjectAreaCoords = {
    x: number;
    y: number;
    width: number;
    height: number;
    alignment: number;
    layoutPositioned: number;
    padTop: number;
    padRight: number;
    padBottom: number;
    padLeft: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
    marginLeft: number;
    hitarea: number;
};

/**
 * Highlight element
 */
export type ElementAlignment =
    | 'default'
    | 'top_left'
    | 'top_mid'
    | 'top_right'
    | 'bottom_left'
    | 'bottom_mid'
    | 'bottom_right'
    | 'left_mid'
    | 'right_mid'
    | 'center';

export type HighlightElement = {
    fullPath: string;
    elementName: string;
    codeLine: number;
    multiLineLength?: number;
    x: number;
    y: number;
    width: number;
    height: number;
    attributeX: number;
    attributeY: number;
    alignment: ElementAlignment;
    layoutPositioned: number;
    paddings: number[];
    margins: number[];
    hitarea: number;
    isSlot?: boolean;
};

export type Subject = {
    name: string;
    type: string;
    value: string | number;
    minValue?: string;
    maxValue?: string;
};

export type PureCoordinates = {
    x: number;
    y: number;
};

export type PropToUpdate = {
    lineNumber: number;
    multiLineLength?: number;
    fullPath: string;
    deltaX?: number;
    deltaY?: number;
    width?: number;
    height?: number;
    dragStartPos?: PureCoordinates;
    layoutPositioned: number;
};

export type DragPosition = {
    mouseX: number;
    mouseY: number;
    targetElement: HTMLDivElement | null;
    elementData: HighlightElement | null;
};

// Options for generating project code
export type ResourceConvertOptions = {
    ignoreFonts?: boolean;
    ignoreImages?: boolean;
};
